/**
 * Settings → Document Types (T-009).
 *
 * The catalogue that drives the FILES matrix columns and the compliance
 * rollup, plus the warning-period rules that decide when a document counts as
 * "expiring soon".
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
import {
  createDocumentType,
  deleteDocumentType,
  fetchDocumentTypes,
  fetchWarningPeriods,
  saveWarningPeriods,
  updateDocumentType,
  type DocumentTypeRecord,
} from '@/lib/documentsApi';
import {
  DEFAULT_WARNING_DAYS,
  explainResolution,
  resolveWarningDays,
  type WarningPeriodRule,
} from '@/lib/warningPeriods';

interface DraftRule extends WarningPeriodRule {
  key: string;
}

let ruleSeq = 0;

export function DocumentTypes() {
  const [types, setTypes] = useState<DocumentTypeRecord[]>([]);
  const [rules, setRules] = useState<DraftRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, p] = await Promise.all([fetchDocumentTypes(), fetchWarningPeriods()]);
      setTypes(t);
      setRules(p.map((r) => {
        ruleSeq += 1;
        return {
          key: `r${ruleSeq}`,
          companyId: r.company_id,
          documentTypeId: r.document_type_id,
          warningDays: r.warning_days,
        };
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(type: DocumentTypeRecord, change: Partial<DocumentTypeRecord>) {
    try {
      const updated = await updateDocumentType(type.id, change);
      setTypes((prev) => prev.map((t) => (t.id === type.id ? { ...t, ...updated } : t)));
      toast.success(`Updated ${type.label}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function create() {
    if (!newKey.trim() || !newLabel.trim()) return;
    try {
      const created = await createDocumentType({ key: newKey.trim(), label: newLabel.trim() });
      setTypes((prev) => [...prev, created]);
      setNewKey('');
      setNewLabel('');
      toast.success(`Created ${created.label}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the document type');
    }
  }

  async function remove(type: DocumentTypeRecord) {
    try {
      const res = await deleteDocumentType(type.id);
      if (res.retired) {
        // Not a failure: the type is still referenced by compliance records, so
        // it is retired rather than destroyed. Say so plainly.
        toast.success(
          `${type.label} retired — ${res.referencingDocuments} document(s) still reference it, so it was not deleted`,
        );
        setTypes((prev) => prev.map((t) => (t.id === type.id ? { ...t, is_active: false } : t)));
      } else {
        toast.success(`${type.label} deleted`);
        setTypes((prev) => prev.filter((t) => t.id !== type.id));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function persistRules(next: DraftRule[]) {
    setSaving(true);
    try {
      await saveWarningPeriods(next.map((r) => ({
        company_id: r.companyId,
        document_type_id: r.documentTypeId,
        warning_days: r.warningDays,
      })));
      setRules(next);
      toast.success('Warning periods saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save warning periods');
    } finally {
      setSaving(false);
    }
  }

  // Preview per active type, so the effect of a rule is visible before saving.
  const previews = useMemo(
    () => types.filter((t) => t.is_active && t.has_expiry).map((t) => ({
      type: t,
      resolution: resolveWarningDays(rules, null, t.id),
    })),
    [types, rules],
  );

  if (loading) return <p className="p-4 text-xs text-muted-foreground">Loading document types…</p>;
  if (error) {
    return (
      <div className="m-4 rounded border border-destructive/40 bg-destructive/10 p-4">
        <p className="text-xs text-destructive">{error}</p>
        <button type="button" onClick={() => void load()} className="mt-2 rounded border border-border px-2.5 py-1 text-xs">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="document-types-settings">
      <header className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Document types</h1>
        <p className="text-xs text-muted-foreground">
          These drive the Files matrix columns and the compliance rollup. Deactivating a type removes its
          column and its contribution to the rollup — units are not marked non-compliant for it. Listed
          when missing: a document on file always shows on the client’s booking; this decides whether a
          missing one is listed there as “Not on file yet”.
        </p>
      </header>

      <div className="mb-6 overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-2 font-medium">Label</th>
            <th className="py-2 font-medium">Key</th>
            <th className="py-2 text-center font-medium">Required</th>
            <th className="py-2 text-center font-medium">Expires</th>
            <th className="py-2 text-center font-medium">Active</th>
            <th className="py-2 text-center font-medium">Listed when missing</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type.id} className="border-b border-border/50" data-testid={`type-${type.key}`}>
              <td className="py-2 text-foreground">{type.label}</td>
              <td className="py-2">
                {/* Immutable: it is the join key behind every document. */}
                <span className="font-mono text-muted-foreground" title="Cannot be changed after creation">
                  {type.key}
                </span>
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={type.is_required}
                  aria-label={`${type.label} required`}
                  onChange={(e) => void patch(type, { is_required: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[#8FB560]"
                />
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={type.has_expiry}
                  aria-label={`${type.label} has expiry`}
                  onChange={(e) => void patch(type, { has_expiry: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[#8FB560]"
                />
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={type.is_active}
                  aria-label={`${type.label} active`}
                  onChange={(e) => void patch(type, { is_active: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[#8FB560]"
                />
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={type.portal_visible !== false}
                  aria-label={`${type.label} listed in the client portal when missing`}
                  onChange={(e) => void patch(type, { portal_visible: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[#8FB560]"
                />
              </td>
              <td className="py-2 text-right">
                <button
                  type="button"
                  aria-label={`Delete ${type.label}`}
                  onClick={() => void remove(type)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-2">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Label"
          aria-label="New type label"
          className="h-7 rounded border border-border bg-transparent px-2 text-xs text-foreground"
        />
        <input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="key_name"
          aria-label="New type key"
          className="h-7 rounded border border-border bg-transparent px-2 font-mono text-xs text-foreground"
        />
        <button
          type="button"
          onClick={() => void create()}
          disabled={!newKey.trim() || !newLabel.trim()}
          className="inline-flex items-center gap-1 rounded bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add type
        </button>
        <span className="text-[11px] text-muted-foreground">The key cannot be changed later.</span>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-foreground">Expiry warning periods</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          How many days before expiry a document starts reading as “expiring soon”. The most specific
          rule wins — company and type beats company, which beats type, which beats the global
          override. With no rule at all the default is {DEFAULT_WARNING_DAYS} days.
        </p>

        {rules.map((rule, i) => (
          <div key={rule.key} className="mb-1.5 flex items-center gap-2" data-testid={`rule-${i}`}>
            <select
              aria-label={`Rule ${i + 1} document type`}
              value={rule.documentTypeId ?? ''}
              onChange={(e) =>
                setRules((prev) => prev.map((r) =>
                  r.key === rule.key ? { ...r, documentTypeId: e.target.value || null } : r))
              }
              className="h-7 rounded border border-border bg-transparent px-1.5 text-xs text-foreground"
            >
              <option value="">All document types</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input
              type="number"
              min={0}
              max={365}
              aria-label={`Rule ${i + 1} warning days`}
              value={rule.warningDays}
              onChange={(e) =>
                setRules((prev) => prev.map((r) =>
                  r.key === rule.key ? { ...r, warningDays: Number(e.target.value) } : r))
              }
              className="h-7 w-20 rounded border border-border bg-transparent px-1.5 text-xs tabular-nums text-foreground"
            />
            <span className="text-[11px] text-muted-foreground">days</span>
            <button
              type="button"
              aria-label={`Remove rule ${i + 1}`}
              onClick={() => setRules((prev) => prev.filter((r) => r.key !== rule.key))}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              ruleSeq += 1;
              setRules((prev) => [...prev, { key: `r${ruleSeq}`, companyId: null, documentTypeId: null, warningDays: DEFAULT_WARNING_DAYS }]);
            }}
            className="rounded border border-border px-2.5 py-1 text-xs text-foreground"
          >
            Add rule
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void persistRules(rules)}
            className="rounded bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save warning periods'}
          </button>
        </div>

        <div className="mt-4" data-testid="resolution-preview">
          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            What each type resolves to
          </h3>
          {previews.length === 0 && (
            <p className="text-[11px] text-muted-foreground">No active types carry an expiry.</p>
          )}
          {previews.map(({ type, resolution }) => (
            <p key={type.id} className="text-[11px] text-muted-foreground" data-testid={`preview-${type.key}`}>
              <span className="text-foreground">{type.label}</span>
              {': '}
              <span className={cn('tabular-nums', resolution.scope === 'default' && 'italic')}>
                {resolution.warningDays} days
              </span>
              {' — from '}
              {explainResolution(resolution)}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
}

export default DocumentTypes;
